import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Calendar, CheckCircle, Circle, Clock, Pencil, Trash2, XCircle, Link } from 'lucide-react';
import type { Milestone } from '../hooks/useMilestones';

type MilestoneCardProps = {
  milestone: Milestone;
  onUpdate: (milestoneId: string, updates: Partial<Milestone>) => Promise<void>;
  onDelete: (milestoneId: string) => Promise<void>;
  isAuthor: boolean;
};

const STATUS_ICONS = {
  planned: Circle,
  in_progress: Clock,
  completed: CheckCircle
};

const STATUS_COLORS = {
  planned: 'text-gray-500',
  in_progress: 'text-indigo-500',
  completed: 'text-green-500'
};

export default function MilestoneCard({
  milestone,
  onUpdate,
  onDelete,
  isAuthor
}: MilestoneCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(milestone.title);
  const [description, setDescription] = useState(milestone.description || '');
  const [status, setStatus] = useState(milestone.status);
  const [targetDate, setTargetDate] = useState(milestone.target_date || '');
  const [purchaseLink, setPurchaseLink] = useState(milestone.purchase_link || '');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const StatusIcon = STATUS_ICONS[milestone.status];

  const handleSave = async () => {
    try {
      await onUpdate(milestone.id, {
        title,
        description: description || null,
        status,
        target_date: targetDate || null,
        purchase_link: purchaseLink || null
      });
      setIsEditing(false);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update milestone');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this milestone? This action cannot be undone.')) {
      return;
    }

    try {
      setIsDeleting(true);
      await onDelete(milestone.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete milestone');
      setIsDeleting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
          {error}
        </div>
      )}

      {isEditing ? (
        <div className="space-y-4">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            placeholder="Milestone title"
            required
          />

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            rows={3}
            placeholder="Description (optional)"
          />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Milestone['status'])}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="planned">Planned</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Target Date
              </label>
              <input
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          {status === 'completed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Purchase Link
              </label>
              <div className="relative">
                <Link className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="url"
                  value={purchaseLink}
                  onChange={(e) => setPurchaseLink(e.target.value)}
                  placeholder="https://..."
                  className="w-full pl-10 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Add a link where readers can purchase the book
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!title}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              Save Changes
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <StatusIcon className={`w-5 h-5 mt-1 ${STATUS_COLORS[milestone.status]}`} />
              <div>
                <h3 className="text-lg font-semibold h3-font">{milestone.title}</h3>
                {milestone.description && (
                  <p className="text-gray-600 mt-1">{milestone.description}</p>
                )}
                <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                  {milestone.target_date && (
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Target: {new Date(milestone.target_date).toLocaleDateString()}
                    </span>
                  )}
                  <span>
                    Created {formatDistanceToNow(new Date(milestone.created_at), { addSuffix: true })}
                  </span>
                </div>
              </div>
            </div>

            {isAuthor && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsEditing(true)}
                  className="p-1 text-gray-500 hover:text-gray-700"
                >
                  <Pencil className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="p-1 text-red-500 hover:text-red-700 disabled:opacity-50"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>

          {milestone.status === 'completed' && milestone.purchase_link && (
            <div className="mt-4">
              <a
                href={milestone.purchase_link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Link className="w-4 h-4" />
                Purchase Book
              </a>
            </div>
          )}
        </>
      )}
    </div>
  );
}